(() => {
  const memoryRootSelector = '[data-about-memory-root]';
  const memoryRailSelector = '.ode-about__memory-rail';
  const memorySelector = '.ode-about__memory';
  const popupRootSelector = '[data-about-popup-root]';
  const popupGallerySelector = '[data-about-popup-gallery]';
  const popupImageSelector = '[data-about-popup-image]';
  const mainMediaSelector = '.ode-about__main-media';

  const initMemoryRail = (root) => {
    const rail = root.querySelector(memoryRailSelector);

    if (!rail || rail.dataset.memoryRailInitialized) return;

    rail.dataset.memoryRailInitialized = 'true';
    const memories = rail.querySelectorAll(memorySelector);
    const canUseMobileMemoryRail = window.matchMedia('(max-width: 640px)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!canUseMobileMemoryRail) {
      let memoryRailFrame;
      let activeMemory = rail.querySelector('.is-memory-active') || memories[0];

      const positionTextAtExpandedLocation = (memory) => {
        const memoryIndex = Array.from(memories).indexOf(memory);

        if (memoryIndex < 0) return;

        const railBounds = rail.getBoundingClientRect();
        const memoryBounds = memory.getBoundingClientRect();
        const measurementRail = rail.cloneNode(true);
        const measurementMemories = measurementRail.querySelectorAll(memorySelector);

        measurementRail.style.position = 'absolute';
        measurementRail.style.visibility = 'hidden';
        measurementRail.style.pointerEvents = 'none';
        measurementRail.style.width = `${railBounds.width}px`;
        measurementRail.style.height = 'auto';
        measurementRail.style.left = '0';
        measurementRail.style.top = '0';

        measurementMemories.forEach((item, index) => {
          item.classList.toggle('is-memory-active', index === memoryIndex);
          item.style.transition = 'none';
        });

        root.appendChild(measurementRail);

        const measurementRailBounds = measurementRail.getBoundingClientRect();
        const expandedBounds = measurementMemories[memoryIndex].getBoundingClientRect();
        const currentLeft = memoryBounds.left - railBounds.left;
        const expandedLeft = expandedBounds.left - measurementRailBounds.left;
        const horizontalShift = expandedLeft - currentLeft;

        measurementRail.remove();

        memory
          .querySelectorAll('.ode-about__memory-label, .ode-about__memory-caption, .ode-about__memory-info')
          .forEach((textElement) => {
            textElement.getAnimations().forEach((animation) => animation.cancel());

            if (Math.abs(horizontalShift) < 0.5) return;

            textElement.animate(
              [
                { transform: `translateX(${horizontalShift}px)` },
                { transform: 'translateX(0)' }
              ],
              {
                duration: 760,
                easing: 'cubic-bezier(.22, 1, .36, 1)'
              }
            );
          });
      };

      const activateMemory = (memory) => {
        if (!memory || memory === activeMemory) return;

        positionTextAtExpandedLocation(memory);
        memories.forEach((item) => item.classList.toggle('is-memory-active', item === memory));
        activeMemory = memory;
      };

      const updateMemoryRailHeight = () => {
        const activeMemory = rail.querySelector('.is-memory-active') || memories[0];

        if (!activeMemory) return;

        const activeWidth = activeMemory.getBoundingClientRect().width;
        let tallestMemory = 0;

        memories.forEach((memory) => {
          const probe = memory.cloneNode(true);

          probe.classList.add('is-memory-active');
          probe.removeAttribute('id');
          probe.style.position = 'absolute';
          probe.style.visibility = 'hidden';
          probe.style.opacity = '0';
          probe.style.pointerEvents = 'none';
          probe.style.width = `${activeWidth}px`;
          probe.style.flex = 'none';
          probe.style.margin = '0';
          rail.appendChild(probe);
          tallestMemory = Math.max(tallestMemory, probe.getBoundingClientRect().height);
          probe.remove();
        });

        rail.style.height = `${Math.ceil(tallestMemory)}px`;
        memoryRailFrame = null;
      };

      const requestMemoryRailHeightUpdate = () => {
        if (!memoryRailFrame) memoryRailFrame = window.requestAnimationFrame(updateMemoryRailHeight);
      };

      root.memoryRailResizeHandler = requestMemoryRailHeightUpdate;
      window.addEventListener('resize', requestMemoryRailHeightUpdate, { passive: true });

      if (document.fonts) {
        document.fonts.ready.then(requestMemoryRailHeightUpdate);
      }

      requestMemoryRailHeightUpdate();

      rail.addEventListener('pointermove', (event) => {
        const hoveredMemory = event.target.closest(memorySelector);

        if (hoveredMemory && rail.contains(hoveredMemory)) activateMemory(hoveredMemory);
      }, { passive: true });

      rail.addEventListener('pointerleave', () => {
        activateMemory(memories[0]);
      });
    }

    if (canUseMobileMemoryRail && !prefersReducedMotion) {
      const originalMemoryList = Array.from(memories);
      const originalMemoryCount = originalMemoryList.length;
      const activeOriginalIndex = Math.max(0, originalMemoryList.findIndex((memory) => memory.classList.contains('is-memory-active')));
      const beforeMemories = document.createDocumentFragment();
      let animationFrame;
      let loopResetTimeout;
      let memoryRailHeightFrame;
      let swipeStartX = 0;
      let swipeStartY = 0;
      let isTrackingSwipe = false;

      originalMemoryList.forEach((memory) => {
        const beforeMemory = memory.cloneNode(true);
        const afterMemory = memory.cloneNode(true);

        beforeMemory.removeAttribute('id');
        afterMemory.removeAttribute('id');
        beforeMemories.appendChild(beforeMemory);
        rail.appendChild(afterMemory);
      });

      rail.insertBefore(beforeMemories, originalMemoryList[0]);

      const memoryList = Array.from(rail.querySelectorAll(memorySelector));
      let activeMemoryIndex = originalMemoryCount + activeOriginalIndex;

      const syncMemoryState = () => {
        memoryList.forEach((memory, index) => {
          memory.classList.toggle('is-memory-active', index === activeMemoryIndex);
        });
      };

      const updateActiveTextOffset = () => {
        const activeMemory = memoryList[activeMemoryIndex];

        if (!activeMemory) return;

        const railBounds = rail.getBoundingClientRect();
        const activeMemoryBounds = activeMemory.getBoundingClientRect();
        const offset = railBounds.left - activeMemoryBounds.left;

        activeMemory.style.setProperty('--memory-text-offset', `${offset}px`);
      };

      const resetInactiveTextOffsets = () => {
        memoryList.forEach((memory, index) => {
          if (index !== activeMemoryIndex) memory.style.removeProperty('--memory-text-offset');
        });
      };

      const updateMemoryRailHeight = () => {
        const railBounds = rail.getBoundingClientRect();
        let tallestMemory = 0;

        for (let index = 0; index < originalMemoryCount; index += 1) {
          const measurementRail = rail.cloneNode(true);
          const measurementMemories = measurementRail.querySelectorAll(memorySelector);
          const activeIndex = originalMemoryCount + index;

          measurementRail.classList.add('is-loop-resetting');
          measurementRail.style.position = 'absolute';
          measurementRail.style.visibility = 'hidden';
          measurementRail.style.pointerEvents = 'none';
          measurementRail.style.width = `${railBounds.width}px`;
          measurementRail.style.height = 'auto';
          measurementRail.style.left = '0';
          measurementRail.style.top = '0';

          measurementMemories.forEach((memory, memoryIndex) => {
            memory.classList.toggle('is-memory-active', memoryIndex === activeIndex);
          });

          root.appendChild(measurementRail);
          tallestMemory = Math.max(tallestMemory, measurementRail.getBoundingClientRect().height);
          measurementRail.remove();
        }

        rail.style.height = `${Math.ceil(tallestMemory)}px`;
        memoryRailHeightFrame = null;
      };

      const requestMemoryRailHeightUpdate = () => {
        if (!memoryRailHeightFrame) memoryRailHeightFrame = window.requestAnimationFrame(updateMemoryRailHeight);
      };

      const getActiveScrollLeft = () => {
        const railBounds = rail.getBoundingClientRect();
        const measurementRail = rail.cloneNode(true);
        const measurementMemories = measurementRail.querySelectorAll(memorySelector);

        measurementRail.classList.add('is-loop-resetting');
        measurementRail.style.position = 'absolute';
        measurementRail.style.visibility = 'hidden';
        measurementRail.style.pointerEvents = 'none';
        measurementRail.style.width = `${railBounds.width}px`;
        measurementRail.style.left = '0';
        measurementRail.style.top = '0';
        measurementRail.scrollLeft = 0;

        measurementMemories.forEach((memory, index) => {
          memory.classList.toggle('is-memory-active', index === activeMemoryIndex);
        });

        root.appendChild(measurementRail);

        const measurementRailBounds = measurementRail.getBoundingClientRect();
        const activeMemoryBounds = measurementMemories[activeMemoryIndex]?.getBoundingClientRect();
        const scrollLeft = activeMemoryBounds ? activeMemoryBounds.left - measurementRailBounds.left : rail.scrollLeft;

        measurementRail.remove();

        return scrollLeft;
      };

      const scrollToActiveMemory = (behavior = 'smooth') => {
        window.cancelAnimationFrame(animationFrame);

        const targetScrollLeft = getActiveScrollLeft();

        if (behavior === 'auto') {
          rail.scrollLeft = targetScrollLeft;
          updateActiveTextOffset();
          resetInactiveTextOffsets();
          return;
        }

        const duration = 520;
        const startScrollLeft = rail.scrollLeft;
        const scrollDistance = targetScrollLeft - startScrollLeft;
        const startedAt = performance.now();

        const animate = (now) => {
          const progress = Math.min((now - startedAt) / duration, 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);

          rail.scrollLeft = startScrollLeft + scrollDistance * easedProgress;
          updateActiveTextOffset();

          if (progress < 1) {
            animationFrame = window.requestAnimationFrame(animate);
          } else {
            resetInactiveTextOffsets();
          }
        };

        animationFrame = window.requestAnimationFrame(animate);
      };

      const normalizeLoopPosition = () => {
        const normalizedIndex = ((activeMemoryIndex % originalMemoryCount) + originalMemoryCount) % originalMemoryCount + originalMemoryCount;

        if (normalizedIndex === activeMemoryIndex) return;

        rail.classList.add('is-loop-resetting');
        activeMemoryIndex = normalizedIndex;
        syncMemoryState();
        scrollToActiveMemory('auto');

        rail.getBoundingClientRect();
        window.requestAnimationFrame(() => rail.classList.remove('is-loop-resetting'));
      };

      const activateMemoryAt = (index, behavior = 'smooth') => {
        window.clearTimeout(loopResetTimeout);
        activeMemoryIndex = Math.min(Math.max(index, 0), memoryList.length - 1);
        syncMemoryState();
        updateActiveTextOffset();
        scrollToActiveMemory(behavior);

        if (behavior === 'smooth') {
          loopResetTimeout = window.setTimeout(normalizeLoopPosition, 560);
        } else {
          normalizeLoopPosition();
        }
      };

      const handlePointerDown = (event) => {
        if (event.pointerType === 'mouse') return;

        window.cancelAnimationFrame(animationFrame);
        window.clearTimeout(loopResetTimeout);
        swipeStartX = event.clientX;
        swipeStartY = event.clientY;
        isTrackingSwipe = true;

        if (rail.setPointerCapture) rail.setPointerCapture(event.pointerId);
      };

      const handlePointerMove = (event) => {
        if (!isTrackingSwipe) return;

        const xDelta = event.clientX - swipeStartX;
        const yDelta = event.clientY - swipeStartY;

        if (Math.abs(xDelta) > Math.abs(yDelta) && Math.abs(xDelta) > 8) {
          event.preventDefault();
        }
      };

      const handlePointerEnd = (event) => {
        if (!isTrackingSwipe) return;

        const xDelta = event.clientX - swipeStartX;
        const yDelta = event.clientY - swipeStartY;
        const minimumSwipe = 34;

        isTrackingSwipe = false;

        if (Math.abs(xDelta) <= Math.abs(yDelta) || Math.abs(xDelta) < minimumSwipe) {
          scrollToActiveMemory();
          return;
        }

        activateMemoryAt(activeMemoryIndex + (xDelta < 0 ? 1 : -1));
      };

      root.memoryRailResizeHandler = () => {
        requestMemoryRailHeightUpdate();
        scrollToActiveMemory('auto');
      };
      rail.classList.add('is-scroll-animated');
      rail.addEventListener('pointerdown', handlePointerDown, { passive: true });
      rail.addEventListener('pointermove', handlePointerMove, { passive: false });
      rail.addEventListener('pointerup', handlePointerEnd, { passive: true });
      rail.addEventListener('pointercancel', () => {
        isTrackingSwipe = false;
        scrollToActiveMemory();
      }, { passive: true });
      window.addEventListener('resize', root.memoryRailResizeHandler, { passive: true });
      if (document.fonts) {
        document.fonts.ready.then(requestMemoryRailHeightUpdate);
      }

      requestMemoryRailHeightUpdate();
      activateMemoryAt(activeMemoryIndex, 'auto');
    }
  };

  const initPopupGallery = (root) => {
    const popupGallery = root.querySelector(popupGallerySelector);
    const popupImages = popupGallery
      ? Array.from(popupGallery.querySelectorAll(popupImageSelector))
      : [];
    const mainMedia = root.querySelector(mainMediaSelector);

    if (root.dataset.popupGalleryInitialized || !popupGallery || !popupImages.length || !mainMedia) return;

    root.dataset.popupGalleryInitialized = 'true';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canUseDesktopPopupGallery = window.matchMedia('(min-width: 641px) and (hover: hover)').matches;
    const canUseMobilePopupGallery = window.matchMedia('(max-width: 640px)').matches;

    if (prefersReducedMotion || (!canUseDesktopPopupGallery && !canUseMobilePopupGallery)) return;

    let nextImage = 0;
    let lastPopupAt = 0;

    const popImage = (image, pointerEvent, isMobile = false) => {
      const bounds = root.getBoundingClientRect();
      const mediaBounds = mainMedia.getBoundingClientRect();
      const imageHeight = (isMobile ? 58 : 70) + Math.round(Math.random() * (isMobile ? 42 : 90));
      const imageWidth = Math.round(imageHeight * 0.805);
      const pointerX = pointerEvent ? pointerEvent.clientX - bounds.left : mediaBounds.left - bounds.left + mediaBounds.width / 2;
      const pointerY = pointerEvent ? pointerEvent.clientY - bounds.top : mediaBounds.top - bounds.top + mediaBounds.height / 2;
      const mediaX = mediaBounds.left - bounds.left;
      const mediaY = mediaBounds.top - bounds.top;
      const gap = 16 + Math.random() * 24;
      const sides = [
        { name: 'left', distance: pointerX - mediaX, fits: mediaX - imageWidth - gap >= 0 },
        { name: 'right', distance: mediaX + mediaBounds.width - pointerX, fits: mediaX + mediaBounds.width + imageWidth + gap <= bounds.width },
        { name: 'top', distance: pointerY - mediaY, fits: mediaY - imageHeight - gap >= 0 },
        { name: 'bottom', distance: mediaY + mediaBounds.height - pointerY, fits: mediaY + mediaBounds.height + imageHeight + gap <= bounds.height },
      ].filter((side) => side.fits).sort((a, b) => a.distance - b.distance);

      const hasCollision = (x, y) => popupImages.some((popupImage) => {
        if (popupImage === image || !popupImage.classList.contains('is-popping') || !popupImage.popupBounds) return false;

        const other = popupImage.popupBounds;
        const padding = 18;
        return x < other.x + other.width + padding
          && x + imageWidth + padding > other.x
          && y < other.y + other.height + padding
          && y + imageHeight + padding > other.y;
      });
      let placement;

      for (let attempt = 0; attempt < 12; attempt += 1) {
        if (isMobile) {
          const x = Math.random() * Math.max(0, bounds.width - imageWidth);
          const y = Math.min(
            Math.max(mediaY - imageHeight * .45 + Math.random() * (mediaBounds.height - imageHeight * .1), 0),
            bounds.height - imageHeight,
          );

          if (!hasCollision(x, y)) {
            placement = { x, y };
            break;
          }

          continue;
        }

        if (!sides.length) return;

        const side = sides[Math.floor(Math.random() * Math.min(3, sides.length))];
        const offset = (Math.random() - 0.5) * 140;
        let candidateX;
        let candidateY;

        if (side.name === 'left') {
          candidateX = mediaX - imageWidth - gap;
          candidateY = pointerY - imageHeight / 2 + offset;
        } else if (side.name === 'top') {
          candidateX = pointerX - imageWidth / 2 + offset;
          candidateY = mediaY - imageHeight - gap;
        } else if (side.name === 'bottom') {
          candidateX = pointerX - imageWidth / 2 + offset;
          candidateY = mediaY + mediaBounds.height + gap;
        } else {
          candidateX = mediaX + mediaBounds.width + gap;
          candidateY = pointerY - imageHeight / 2 + offset;
        }

        const x = Math.min(Math.max(candidateX, 0), bounds.width - imageWidth);
        const y = Math.min(Math.max(candidateY, 0), bounds.height - imageHeight);

        if (!hasCollision(x, y)) {
          placement = { x, y };
          break;
        }
      }

      if (!placement) return;

      const { x, y } = placement;

      image.style.setProperty('--popup-x', `${x}px`);
      image.style.setProperty('--popup-y', `${y}px`);
      image.style.setProperty('--popup-width', `${imageWidth}px`);
      image.style.setProperty('--popup-height', `${imageHeight}px`);
      image.popupBounds = { x, y, width: imageWidth, height: imageHeight };
      image.classList.remove('is-popping');
      void image.offsetWidth;
      image.classList.add('is-popping');

      window.clearTimeout(image.popupTimeout);
      image.popupTimeout = window.setTimeout(() => {
        image.classList.remove('is-popping');
        image.popupBounds = null;
      }, 900);
    };

    if (canUseDesktopPopupGallery) {
      mainMedia.addEventListener('pointerenter', (event) => {
        const visibleImages = Math.min(3, popupImages.length);

        for (let index = 0; index < visibleImages; index += 1) {
          popImage(popupImages[index], event);
        }

        nextImage = visibleImages % popupImages.length;
      });

      mainMedia.addEventListener('pointermove', (event) => {
        const now = performance.now();

        if (now - lastPopupAt < 220) return;

        lastPopupAt = now;
        popImage(popupImages[nextImage], event);
        nextImage = (nextImage + 1) % popupImages.length;
      });
    }

    if (canUseMobilePopupGallery && 'IntersectionObserver' in window) {
      const popupObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        popupImages.slice(0, Math.min(4, popupImages.length)).forEach((image, index) => {
          window.setTimeout(() => popImage(image, null, true), index * 180);
        });

        popupObserver.disconnect();
      }, {
        rootMargin: '-49% 0px -50% 0px',
        threshold: 0,
      });

      popupObserver.observe(mainMedia);
    }
  };

  const initPopupGalleries = (scope = document) => {
    if (scope.matches?.(popupRootSelector)) {
      initPopupGallery(scope);
    }

    scope.querySelectorAll(popupRootSelector).forEach(initPopupGallery);
  };

  const initMemoryRails = (scope = document) => {
    if (scope.matches?.(memoryRootSelector)) {
      initMemoryRail(scope);
    }

    scope.querySelectorAll(memoryRootSelector).forEach(initMemoryRail);
  };

  const initAboutSections = (scope = document) => {
    initMemoryRails(scope);
    initPopupGalleries(scope);
  };

  const destroyAboutSections = (scope = document) => {
    const roots = [];

    if (scope.matches?.(memoryRootSelector)) {
      roots.push(scope);
    }

    scope.querySelectorAll(memoryRootSelector).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root.memoryRailResizeHandler) {
        window.removeEventListener('resize', root.memoryRailResizeHandler);
        root.memoryRailResizeHandler = null;
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAboutSections(), { once: true });
  } else {
    initAboutSections();
  }

  document.addEventListener('shopify:section:load', (event) => initAboutSections(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAboutSections(event.target));
})();
